import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import "./App.css";
import toast, { Toaster } from 'react-hot-toast';

// Import authentication components
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import AuthPage from "./components/auth/AuthPage";
import ForgotPasswordForm from "./components/auth/ForgotPasswordForm";
import ResetPasswordForm from "./components/auth/ResetPasswordForm";
import ProtectedRoute from "./components/ProtectedRoute";

// Import page components
import ServiceLogsPage from "./components/ServiceLogsPage";
import EnhancedPropertiesPage from "./components/EnhancedPropertiesPage";
import GeolocationPage from "./components/GeolocationPage";
import HealthSafetyPage from "./components/HealthSafetyPage";
import EmployeeEngagementPage from "./components/EmployeeEngagementPage";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import SettingsPage from "./components/SettingsPage";
import MessagesPage from "./components/MessagesPage";
import AssignmentManagement from "./components/admin/AssignmentManagement";
import { VisitorDashboard } from "./components/visitor";
import DashboardPage from "./pages/DashboardPage";
import AssetsPage from "./pages/AssetsPage";
import UsersPage from "./pages/UsersPage";
import AccountPage from "./pages/AccountPage";

// Import layout components
import SidebarNavigation from "./layout/SidebarNavigation";
import TopBar from "./layout/TopBar";

// Import hooks
import useNotifications from "./hooks/useNotifications";

function MainApp() {
  const { user } = useAuth();

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Notification UI state
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  // Notification data from hook
  const {
    notifications,
    notificationLoading,
    notificationError,
    fetchNotifications,
    markAsRead: markNotificationAsRead,
    clearAll: clearAllNotifications,
  } = useNotifications();

  // Check if we're on mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth < 768;
      setIsMobile(newIsMobile);
      if (newIsMobile) {
        setSidebarOpen(false);
        setSidebarCollapsed(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isNotificationOpen && !event.target.closest('.notification-container')) {
        setIsNotificationOpen(false);
      }
      if (isUserMenuOpen && !event.target.closest('.user-menu-container')) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isNotificationOpen, isUserMenuOpen]);

  const toggleNotifications = () => {
    setIsNotificationOpen(!isNotificationOpen);
    if (!isNotificationOpen && notifications.length === 0) {
      fetchNotifications();
    }
  };

  // Register service worker for PWA
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
          .then((registration) => {
            console.log('📱 PWA: Service Worker registered successfully:', registration);
            registration.addEventListener('updatefound', () => {
              console.log('📱 PWA: New service worker version found');
            });
          })
          .catch((registrationError) => {
            console.error('📱 PWA: Service Worker registration failed:', registrationError);
          });
      });
    } else {
      console.warn('📱 PWA: Service Workers not supported');
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <SidebarNavigation
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
      />

      <TopBar
        isMobile={isMobile}
        sidebarCollapsed={sidebarCollapsed}
        setSidebarOpen={setSidebarOpen}
        isNotificationOpen={isNotificationOpen}
        setIsNotificationOpen={setIsNotificationOpen}
        notifications={notifications}
        notificationLoading={notificationLoading}
        notificationError={notificationError}
        toggleNotifications={toggleNotifications}
        fetchNotifications={fetchNotifications}
        markNotificationAsRead={markNotificationAsRead}
        clearAllNotifications={clearAllNotifications}
        isUserMenuOpen={isUserMenuOpen}
        setIsUserMenuOpen={setIsUserMenuOpen}
        user={user}
      />

      <main className={`transition-all duration-300 pt-16 ${isMobile ? 'ml-0' : (sidebarCollapsed ? 'ml-16' : 'ml-64')}`}>
        <div className="p-4 lg:p-6">
          <Routes>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/assets" element={<AssetsPage />} />
            <Route path="/properties" element={<EnhancedPropertiesPage />} />
            <Route path="/visitors" element={<VisitorDashboard />} />
            <Route path="/service-logs" element={<ServiceLogsPage />} />
            <Route path="/geolocation" element={<GeolocationPage />} />
            <Route path="/health-safety" element={<HealthSafetyPage />} />
            <Route path="/engagement" element={<EmployeeEngagementPage />} />
            <Route path="/assignments" element={<AssignmentManagement />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="*" element={<DashboardPage />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            borderRadius: '8px',
            background: '#333',
            color: '#fff',
          },
        }}
      />
      <AuthProvider>
        <Routes>
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordForm />} />
          <Route path="/reset-password" element={<ResetPasswordForm />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/*" element={
            <ProtectedRoute>
              <MainApp />
            </ProtectedRoute>
          } />
        </Routes>

        <PWAInstallPrompt />
      </AuthProvider>
    </Router>
  );
}

export default App;
